import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, LogIn, KeyRound, ArrowRight, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function InviteVerificationPage() {
  const { inviteToken } = useParams();
  const navigate = useNavigate();

  const [step, setStep] = useState('loading'); // loading, login, create-password, error
  const [loading, setLoading] = useState(false);
  const [inviteStatus, setInviteStatus] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const checkInvite = async () => {
      try {
        const res = await fetch(`${API_URL}/api/auth/invite/${inviteToken}/status`);
        const data = await res.json();

        if (!data.valid) {
          setErrorMsg(data.error || 'This invitation link is not recognized.');
          setStep('error');
          return;
        }

        setInviteStatus(data);
        setStep(data.has_password ? 'login' : 'create-password');
      } catch {
        setErrorMsg('Unable to verify the invitation. Please try again later.');
        setStep('error');
      }
    };

    checkInvite();
  }, [inviteToken]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password) return;

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/invite/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invite_token: inviteToken, password }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Login failed');

      localStorage.setItem('pulse_token', data.access_token);
      localStorage.setItem('pulse_user', JSON.stringify(data.user));
      toast.success('Welcome back!');
      navigate(`/project/${inviteStatus?.project_id || ''}/about-me`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePassword = async (e) => {
    e.preventDefault();

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (!agreedToPrivacy) {
      toast.error('You must agree to the Privacy Policy to continue');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/invite/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invite_token: inviteToken, password }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to create account');

      localStorage.setItem('pulse_token', data.access_token);
      localStorage.setItem('pulse_user', JSON.stringify(data.user));
      toast.success('Account created successfully!');
      navigate(`/onboarding/${inviteToken}`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (step === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-[#5AA2AE]" />
          <p className="text-slate-600">Verifying invitation...</p>
        </div>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img src="/pulse-logo-with-name.png" alt="Pulse by ProjectAI" className="h-10 mx-auto" />
          </div>
          <Card className="shadow-xl border-0" data-testid="invite-error-card">
            <CardContent className="pt-8 pb-6 text-center space-y-4">
              <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto">
                <AlertCircle className="w-8 h-8 text-amber-500" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900">Invitation Not Found</h2>
              <p className="text-sm text-slate-600">{errorMsg}</p>
              <div className="pt-2 space-y-2">
                <Link to="/login">
                  <Button className="w-full bg-[#5AA2AE] hover:bg-[#4a8a94]" data-testid="go-to-login-btn">
                    Go to Sign In
                  </Button>
                </Link>
                <p className="text-xs text-slate-400">
                  If you already have an account, you can sign in directly.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/pulse-logo-with-name.png" alt="Pulse by ProjectAI" className="h-10 mx-auto" />
        </div>

        <Card className="shadow-xl border-0" data-testid="invite-verification-card">
          {step === 'login' && (
            <>
              <CardHeader className="text-center pb-2">
                <div className="w-16 h-16 bg-[#5AA2AE]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <LogIn className="w-8 h-8 text-[#5AA2AE]" />
                </div>
                <CardTitle className="text-xl">Welcome Back!</CardTitle>
                <CardDescription className="mt-2">
                  Hi <strong>{inviteStatus?.stakeholder_name}</strong>,
                  sign in to access <strong>{inviteStatus?.project_name}</strong>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="bg-slate-50 p-3 rounded-lg text-sm text-slate-600">
                    {inviteStatus?.email}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <div className="relative">
                      <Input
                        id="login-password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        required
                        className="pr-10"
                        data-testid="invite-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <Button
                    type="submit"
                    disabled={loading || !password}
                    className="w-full bg-[#5AA2AE] hover:bg-[#4a8a94]"
                    data-testid="invite-login-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                    Sign In
                  </Button>
                  <div className="text-center">
                    <Link to="/login" className="text-sm text-[#5AA2AE] hover:underline">
                      Forgot your password? Sign in here
                    </Link>
                  </div>
                </form>
              </CardContent>
            </>
          )}

          {step === 'create-password' && (
            <>
              <CardHeader className="text-center pb-2">
                <div className="w-16 h-16 bg-[#5AA2AE]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <KeyRound className="w-8 h-8 text-[#5AA2AE]" />
                </div>
                <CardTitle className="text-xl">Create Your Password</CardTitle>
                <CardDescription className="mt-2">
                  Welcome, <strong>{inviteStatus?.stakeholder_name}</strong>!
                  <br />
                  Set up your password to access <strong>{inviteStatus?.project_name}</strong>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreatePassword} className="space-y-4">
                  <div className="bg-slate-50 p-3 rounded-lg text-sm text-slate-600">
                    {inviteStatus?.email}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-password">Password</Label>
                    <div className="relative">
                      <Input
                        id="new-password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="At least 8 characters"
                        required
                        minLength={8}
                        className="pr-10"
                        data-testid="invite-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirm Password</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm your password"
                      required
                      data-testid="invite-confirm-password-input"
                    />
                  </div>

                  {password && confirmPassword && password !== confirmPassword && (
                    <div className="flex items-center gap-2 text-red-500 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      Passwords do not match
                    </div>
                  )}

                  <div className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      id="invite-privacy-policy"
                      checked={agreedToPrivacy}
                      onChange={(e) => setAgreedToPrivacy(e.target.checked)}
                      className="rounded border-slate-300 mt-0.5"
                      data-testid="privacy-policy-checkbox"
                    />
                    <label htmlFor="invite-privacy-policy" className="text-sm text-slate-600 leading-tight">
                      I agree to the{' '}
                      <a
                        href="https://projectai.com/privacy-policy/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#5AA2AE] hover:underline font-medium"
                      >
                        Privacy Policy
                      </a>
                    </label>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading || password.length < 8 || password !== confirmPassword || !agreedToPrivacy}
                    className="w-full bg-[#5AA2AE] hover:bg-[#4a8a94]"
                    data-testid="invite-create-account-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                    Create Account & Continue
                  </Button>
                </form>
              </CardContent>
            </>
          )}
        </Card>

        <p className="text-center text-xs text-slate-400 mt-6">
          By continuing, you agree to our{' '}
          <a href="https://projectai.com/privacy-policy/" target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-500">Privacy Policy</a>
        </p>
      </div>
    </div>
  );
}
